import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';

import { isBackendApiUrl } from '@core/http/api-url.util';

import { UserAccountService } from '../account/user-account.service';
import { clearStoredAuthToken, getStoredAuthToken, isStoredTokenGuest } from './auth-token.storage';
import { TenantSessionService } from '../../tenant/state/tenant-session.service';
import {
  isStudentPortalPath,
  resolveStudentLoginPath,
} from '../../tenant/utils/tenant-routing.util';
import { InterviewRuntimeService } from '../../interview/interview/services/interview-runtime.service';

const ROLE_UPDATED_MESSAGE = 'Your role has been updated. Please log in again.';

/** Attaches Bearer token; on 401 when a token was sent, clears session and sends user to login. */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (typeof localStorage === 'undefined') {
    return next(req);
  }

  const router = inject(Router);
  const account = inject(UserAccountService);
  const tenantSession = inject(TenantSessionService);
  const interviewRuntime = inject(InterviewRuntimeService);
  if (!isBackendApiUrl(req.url)) {
    return next(req);
  }

  let authReq = req;
  let wasGuestToken = false;
  const isPublicBlogApi = req.url.includes('/public/blog');
  if (!req.headers.has('Authorization') && !isPublicBlogApi) {
    // Prefer the tab-pinned room JWT while an interview is active in this tab.
    // Another tab may have overwritten localStorage with a different account.
    const pinned = interviewRuntime.getRoomAccessToken();
    const token = pinned ?? getStoredAuthToken();
    if (token) {
      wasGuestToken = isStoredTokenGuest(token);
      authReq = req.clone({
        setHeaders: { Authorization: `Bearer ${token}` },
      });
    }
  }

  if (!authReq.headers.has('X-Tenant-Role') && authReq.url.includes('/tenants/')) {
    const role = tenantSession.session()?.role;
    if (role) {
      authReq = authReq.clone({ setHeaders: { 'X-Tenant-Role': role } });
    }
  }

  const hadAuthHeader = authReq.headers.has('Authorization');

  const authPathSkips401Redirect = (() => {
    const u = authReq.url;
    return (
      u.includes('/auth/google') ||
      u.includes('/auth/login') ||
      u.includes('/auth/register') ||
      u.includes('/auth/admin/login') ||
      u.includes('/auth/admin/session') ||
      u.includes('/c/check') ||
      u.includes('/c/i/') ||
      u.includes('/c/start') ||
      u.includes('/c/consent') ||
      u.includes('/c/room')
    );
  })();

  return next(authReq).pipe(
    catchError((err: unknown) => {
      if (
        err instanceof HttpErrorResponse &&
        err.status === 403 &&
        err.error?.error?.message === ROLE_UPDATED_MESSAGE
      ) {
        const fromUrl =
          typeof location !== 'undefined' && location.pathname
            ? `${location.pathname}${location.search}`
            : router.url || '/';

        // Public blog must never be redirected to portal login.
        if (fromUrl.startsWith('/blog')) {
          return throwError(() => err);
        }

        const slug = tenantSession.session()?.tenantSlug;
        clearStoredAuthToken();
        tenantSession.clearSession();
        const rolePath = isStudentPortalPath(fromUrl)
          ? resolveStudentLoginPath({ tenantSlug: slug, fromUrl })
          : '/portal/login?reason=role-updated';
        void router.navigateByUrl(rolePath);
        return throwError(() => err);
      }

      // Only redirect when the client believed it was signed in (had Bearer).
      // Anonymous API calls must not auto-jump to /login on 401.
      if (err instanceof HttpErrorResponse && err.status === 401) {
        const path =
          typeof location !== 'undefined' && location.pathname
            ? `${location.pathname}${location.search}`
            : router.url || '/';

        // Public blog stays readable with stale tokens — never bounce to login.
        if (path.startsWith('/blog')) {
          return throwError(() => err);
        }
      }

      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        hadAuthHeader &&
        !wasGuestToken &&
        !authPathSkips401Redirect &&
        !authReq.url.includes('/public/blog')
      ) {
        const path =
          typeof location !== 'undefined' && location.pathname
            ? `${location.pathname}${location.search}`
            : router.url || '/';

        // Live interview room: never bounce to /login — that used to run canDeactivate
        // abandon and kill an IN_PROGRESS session while the candidate was still answering.
        const inLiveInterview =
          path.startsWith('/session') || !!interviewRuntime.active();
        if (inLiveInterview) {
          return throwError(() => err);
        }

        clearStoredAuthToken();
        account.clearCreditsCache();

        if (
          !path.startsWith('/portal/login') &&
          !path.startsWith('/portal/student-login') &&
          !path.startsWith('/login') &&
          !path.startsWith('/admin/login') &&
          !/^\/[^/]+\/portal\/student-login/.test(path)
        ) {
          const toAdminLogin = path.startsWith('/admin');
          const isStaffPath = ['/college', '/company', '/student', '/portal', '/invites'].some(
            (prefix) => path.startsWith(prefix),
          ) || /^\/[^/]+\/(college|company|student)(?:\/|$)/.test(path);
          const loginPath = toAdminLogin
            ? '/admin/login'
            : isStudentPortalPath(path)
              ? resolveStudentLoginPath({ fromUrl: path })
              : isStaffPath
                ? '/portal/login'
                : '/login';
          void router.navigateByUrl(
            `${loginPath}?next=${encodeURIComponent(path)}`,
          );
        }
      }
      return throwError(() => err);
    }),
  );
};
